/**
 * Customer API Service
 * 
 * Merkezi API çağrıları için service class'ı
 * Component'ler direkt fetch yapmak yerine bu service'i kullanmalı
 */

// Types
export interface DashboardData {
  reservations_summary: {
    pending: number;
    confirmed: number;
    cancelled: number;
    total: number;
  };
  favorites_count: number;
  recent_activity: Array<{
    type: string;
    title: string;
    date: string;
    status: string;
  }>;
  statistics: {
    total_spent: number;
    total_reservations: number;
  };
  upcoming_payment_dues?: UpcomingPaymentDue[];
  open_payment_dues_count?: number;
}

export interface UpcomingPaymentDue {
  id: number;
  reservation_id?: number;
  reservation_no?: string;
  product_name?: string;
  amount: number;
  late_fee_amount?: number;
  total_due: number;
  due_date?: string | null;
  status?: string;
  payment_url?: string | null;
  currency?: { code?: string; symbol?: string } | null;
}

export interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  birth_date?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  email_verified_at?: string;
  phone_verified_at?: string | null;
  newsletter_subscribed: boolean;
  account_status: string;
  [key: string]: unknown;
}

export interface ProfileUpdateData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  birth_date?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
}

export interface BillingAddress {
  id?: number;
  type: 'individual' | 'corporate';
  title?: string;
  is_default?: boolean;
  first_name?: string;
  last_name?: string;
  tc_no?: string;
  company_name?: string;
  tax_number?: string;
  tax_office?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
}

export interface PasswordUpdateData {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface Favorite {
  id: number;
  product_id: number;
  product_type: string;
  // Backend'den gelen product artık tam product data içeriyor (gallery, metas, currency vb.)
  // ProductSearchController formatProduct mantığı kullanılıyor
  product: {
    id: number;
    name?: string;
    slug?: string;
    type: string;
    url?: string;
    description?: string;
    currency?: {
      id: number;
      code: string;
      symbol: string;
      symbol_position?: string;
      thousands_separator?: string;
      decimal_separator?: string;
    } | null;
    categories?: Array<{
      id: number;
      name: string;
      slug: string;
    }>;
    metas?: Record<string, any>; // Gallery'den oluşturulmuş images, image_alt, image_count, video_count vb. içerir
    gallery?: Array<any>; // Gallery array (backend'den parse edilmiş)
    is_featured?: boolean;
    created_at?: string;
    updated_at?: string;
  } | null;
  created_at: string;
}

export interface FavoritesResponse {
  data: Favorite[];
  current_page: number;
  total: number;
  per_page: number;
  last_page: number;
}

export interface NewsletterStatus {
  subscribed: boolean;
  subscribed_at?: string;
  unsubscribed_at?: string;
}

export interface ReservationListItem {
  id: number;
  reservation_no: string;
  status: string;
  payment_status?: string;
  total_amount?: string | number;
  paid_amount?: string | number;
  reservation_date?: string;
  travel_date?: string | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
  guest_count?: number;
  product?: { id: number; name?: string; slug?: string; type?: string } | null;
  currency?: { id: number; code: string; symbol: string } | null;
  created_at: string;
  open_dues_count?: number;
  [key: string]: unknown;
}

export interface ReservationsResponse {
  data: ReservationListItem[];
  meta?: { pagination?: { page: number; pageSize: number; pageCount: number; total: number } };
}

export interface InvoiceListItem {
  id: number;
  invoice_no?: string;
  status?: string;
  total_amount?: string | number;
  paid_amount?: string | number;
  remaining_amount?: number;
  issue_date?: string | null;
  due_date?: string | null;
  currency?: { id: number; code: string; symbol: string } | null;
  payment_url?: string | null;
  is_payable?: boolean;
  has_pdf?: boolean;
  can_request_payment_link?: boolean;
  reservation_id?: number | null;
  order_id?: number | null;
  [key: string]: unknown;
}

export interface VoucherItem {
  id: number;
  reservation_id: number;
  voucher_no?: string;
  pdf_path?: string | null;
  [key: string]: unknown;
}

export interface ReservationRoomItem {
  adults?: number;
  children?: number;
  child_ages?: number[];
  guests?: Array<{ first_name?: string; last_name?: string; email?: string; phone?: string }>;
  [key: string]: unknown;
}

export interface ReservationParticipantItem {
  id: number;
  participant_type?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  phone?: string;
  birth_date?: string | null;
  passport_no?: string | null;
  nationality?: string | null;
  [key: string]: unknown;
}

export interface ReservationPaymentItem {
  id: number;
  payment_no?: string;
  amount?: string | number;
  status?: string;
  payment_method?: string;
  paid_at?: string | null;
  currency?: { id: number; code: string; symbol: string } | null;
  [key: string]: unknown;
}

export interface ReservationPaymentScheduleItem {
  id: number;
  amount: number;
  late_fee_amount?: number;
  total_due?: number;
  due_date?: string | null;
  status?: string;
  payment_url?: string | null;
}

export interface ReservationDetail extends ReservationListItem {
  vouchers?: VoucherItem[];
  guest_details?: unknown;
  special_requests?: string | null;
  rooms?: ReservationRoomItem[];
  participants?: ReservationParticipantItem[];
  payments?: ReservationPaymentItem[];
  status_history?: unknown;
  payment_schedule?: ReservationPaymentScheduleItem[];
}

class CustomerApiService {
  private static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData: any;
      try {
        const text = await response.text();
        errorData = text ? JSON.parse(text) : { error: 'Unknown error' };
      } catch {
        errorData = { error: 'Unknown error' };
      }
      
      // Hata mesajını temizle - sadece message veya error kısmını göster
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      
      // Validation errors varsa ilk hatayı göster
      if (errorData.errors && Object.keys(errorData.errors).length > 0) {
        const firstErrorKey = Object.keys(errorData.errors)[0];
        const firstError = Array.isArray(errorData.errors[firstErrorKey]) 
          ? errorData.errors[firstErrorKey][0] 
          : errorData.errors[firstErrorKey];
        throw new Error(firstError || errorMessage);
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    if (!data.success) {
      // Hata mesajını temizle - sadece message veya error kısmını göster
      const errorMessage = data.message || data.error || 'Request failed';
      
      // Validation errors varsa ilk hatayı göster
      if (data.errors && Object.keys(data.errors).length > 0) {
        const firstErrorKey = Object.keys(data.errors)[0];
        const firstError = Array.isArray(data.errors[firstErrorKey]) 
          ? data.errors[firstErrorKey][0] 
          : data.errors[firstErrorKey];
        throw new Error(firstError || errorMessage);
      }
      
      throw new Error(errorMessage);
    }

    return data.data || data;
  }

  /**
   * Get dashboard data
   */
  static async getDashboard(): Promise<DashboardData> {
    const response = await fetch('/api/customer/dashboard', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    return this.handleResponse<DashboardData>(response);
  }

  /**
   * Get customer profile
   */
  static async getProfile(): Promise<Customer> {
    const response = await fetch('/api/customer/me', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const data = await this.handleResponse<{ customer: Customer }>(response);
    return data.customer;
  }

  /**
   * Update customer profile
   */
  static async updateProfile(data: ProfileUpdateData): Promise<Customer> {
    const response = await fetch('/api/customer/me', {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await this.handleResponse<{ customer: Customer }>(response);
    return result.customer;
  }

  /**
   * Update password
   */
  static async updatePassword(data: PasswordUpdateData): Promise<void> {
    const response = await fetch('/api/customer/password', {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });

    await this.handleResponse(response);
  }

  /**
   * Upload avatar
   */
  static async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await fetch('/api/customer/avatar', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    return this.handleResponse<{ avatar_url: string }>(response);
  }

  /**
   * Get customer reservations list (paginated)
   */
  static async getReservations(params?: { page?: number; per_page?: number }): Promise<ReservationsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.per_page) queryParams.set('per_page', params.per_page.toString());
    const url = `/api/customer/reservations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const body = await response.json();
    if (!response.ok) {
      const msg = body?.error ?? body?.message ?? 'Rezervasyonlar alınamadı';
      throw new Error(typeof msg === 'string' ? msg : 'Rezervasyonlar alınamadı');
    }
    return {
      data: Array.isArray(body.data) ? body.data : [],
      meta: body.meta ?? {},
    };
  }

  /**
   * Get reservation detail (for modal / voucher view)
   */
  static async getReservationSplitPaymentLink(
    reservationId: number,
    splitId: number
  ): Promise<{ payment_url: string; invoice_id?: number }> {
    const response = await fetch(
      `/api/customer/reservations/${reservationId}/payment-splits/${splitId}/payment-link`,
      { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } }
    );
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body?.error ?? body?.message ?? 'Ödeme linki alınamadı');
    }
    const data = body.data ?? body;
    if (!data?.payment_url) {
      throw new Error('Ödeme linki bulunamadı');
    }
    return data;
  }

  static async getReservationDetail(id: number): Promise<ReservationDetail> {
    const response = await fetch(`/api/customer/reservations/${id}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const body = await response.json();
    if (!response.ok) {
      const msg = body?.error ?? body?.message ?? 'Rezervasyon detayı alınamadı';
      throw new Error(typeof msg === 'string' ? msg : 'Rezervasyon detayı alınamadı');
    }
    return (body.data ?? body) as ReservationDetail;
  }

  /**
   * Voucher PDF URL (site içi görüntüleme / indirme)
   */
  static getVoucherFileUrl(reservationId: number, voucherId: number): string {
    return `/api/reservations/${reservationId}/voucher/${voucherId}/download-file`;
  }

  /**
   * Müşteri fatura listesi (sayfalı)
   */
  static async getInvoices(params?: { page?: number; per_page?: number }): Promise<{ data: InvoiceListItem[]; meta?: { pagination?: { page: number; pageSize: number; pageCount: number; total: number } } }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.per_page) queryParams.set('per_page', params.per_page.toString());
    const url = `/api/customer/invoices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
    const body = await response.json();
    if (!response.ok) {
      const msg = body?.error ?? body?.message ?? 'Faturalar alınamadı';
      throw new Error(typeof msg === 'string' ? msg : 'Faturalar alınamadı');
    }
    return { data: Array.isArray(body.data) ? body.data : [], meta: body.meta ?? {} };
  }

  /**
   * Fatura PDF URL veya indirme bilgisi (API'den pdf_url döner)
   */
  static async getInvoicePdfInfo(id: number): Promise<{ pdf_url: string; invoice_no: string }> {
    const response = await fetch(`/api/customer/invoices/${id}/pdf`, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
    const body = await response.json();
    if (!response.ok) throw new Error(body?.error ?? body?.message ?? 'Fatura PDF alınamadı');
    const data = body.data ?? body;
    if (!data?.pdf_url) throw new Error('Fatura PDF bulunamadı');
    return { pdf_url: data.pdf_url, invoice_no: data.invoice_no ?? `invoice-${id}` };
  }

  static async getInvoicePaymentLink(id: number): Promise<{ payment_url: string; invoice_no?: string }> {
    const response = await fetch(`/api/customer/invoices/${id}/payment-link`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body?.error ?? body?.message ?? 'Ödeme linki alınamadı');
    }
    const data = body.data ?? body;
    if (!data?.payment_url) {
      throw new Error('Ödeme linki bulunamadı');
    }
    return { payment_url: data.payment_url, invoice_no: data.invoice_no };
  }

  /**
   * Get favorites list
   */
  static async getFavorites(params?: {
    per_page?: number;
    product_type?: string;
    page?: number;
  }): Promise<FavoritesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.per_page) queryParams.set('per_page', params.per_page.toString());
    if (params?.product_type) queryParams.set('product_type', params.product_type);
    if (params?.page) queryParams.set('page', params.page.toString());

    const url = `/api/customer/favorites${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    return this.handleResponse<FavoritesResponse>(response);
  }

  /**
   * Toggle favorite (add/remove)
   */
  static async toggleFavorite(productId: number, productType: string): Promise<{ isFavorite: boolean }> {
    const response = await fetch('/api/customer/favorites/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        product_id: productId,
        product_type: productType,
      }),
    });

    const data = await this.handleResponse<{ isFavorite: boolean }>(response);
    return { isFavorite: data.isFavorite };
  }

  /**
   * Check favorite status
   */
  static async checkFavorite(productId: number, productType: string): Promise<{ isFavorite: boolean }> {
    const queryParams = new URLSearchParams({
      product_id: productId.toString(),
      product_type: productType,
    });

    const response = await fetch(`/api/customer/favorites/check?${queryParams.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const data = await this.handleResponse<{ isFavorite: boolean }>(response);
    return { isFavorite: data.isFavorite };
  }

  /**
   * Get billing addresses
   */
  static async getBillingAddresses(): Promise<BillingAddress[]> {
    const response = await fetch('/api/customer/billing-addresses', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const data = await this.handleResponse<BillingAddress[] | { data: BillingAddress[] }>(response);
    return Array.isArray(data) ? data : ((data as { data: BillingAddress[] }).data || []);
  }

  /**
   * Create billing address
   */
  static async createBillingAddress(address: BillingAddress): Promise<BillingAddress> {
    const response = await fetch('/api/customer/billing-addresses', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(address),
    });

    const data = await this.handleResponse<BillingAddress>(response);
    return data;
  }

  /**
   * Update billing address
   */
  static async updateBillingAddress(id: number, address: BillingAddress): Promise<BillingAddress> {
    const response = await fetch(`/api/customer/billing-addresses/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(address),
    });

    const data = await this.handleResponse<BillingAddress>(response);
    return data;
  }

  /**
   * Delete billing address
   */
  static async deleteBillingAddress(id: number): Promise<void> {
    const response = await fetch(`/api/customer/billing-addresses/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }

  /**
   * Set default billing address
   */
  static async setDefaultBillingAddress(id: number): Promise<void> {
    const response = await fetch(`/api/customer/billing-addresses/${id}/set-default`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }

  /**
   * Remove favorite
   */
  static async removeFavorite(favoriteId: number): Promise<void> {
    const response = await fetch(`/api/customer/favorites/${favoriteId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }

  /**
   * Get newsletter status
   */
  static async getNewsletterStatus(): Promise<NewsletterStatus> {
    const response = await fetch('/api/customer/newsletter', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    return this.handleResponse<NewsletterStatus>(response);
  }

  /**
   * Subscribe to newsletter
   */
  static async subscribeNewsletter(): Promise<void> {
    const response = await fetch('/api/customer/newsletter/subscribe', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }

  /**
   * Unsubscribe from newsletter
   */
  static async unsubscribeNewsletter(): Promise<void> {
    const response = await fetch('/api/customer/newsletter/unsubscribe', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }

  /**
   * Delete account
   */
  static async deleteAccount(password: string): Promise<void> {
    const response = await fetch('/api/customer/account', {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    await this.handleResponse(response);
  }

  /**
   * Verify email
   */
  static async verifyEmail(): Promise<void> {
    const response = await fetch('/api/customer/verify-email', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }

  /**
   * Verify phone
   */
  static async verifyPhone(): Promise<void> {
    const response = await fetch('/api/customer/verify-phone', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    await this.handleResponse(response);
  }
}

export default CustomerApiService;

